/**
 * FERP Portal - Report List Page
 *
 * OPTIMIZE phase primitive - Resources.
 * Displays resources from backend via useListResourcesQuery.
 * Resources track capacity, allocation, and system resource health.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Data from backend via useListResourcesQuery
 * - Multi-tenant via useTenantId()
 * - Dumb Frontend: All data from backend, no hardcoded values
 */

import { useMemo, useState } from 'react';
import { useTerminology } from '@ferp/workflow-forms';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Table,
  Paper,
  ThemeIcon,
  Alert,
  Button,
  Loader,
  TextInput,
  Progress,
} from '@mantine/core';
import {
  IconFileAnalytics,
  IconAlertTriangle,
  IconRefresh,
  IconSearch,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import {
  useListResourcesQuery,
  type ResourceFieldsFragment,
} from '@/generated/graphql';

export function ReportListPage() {
  const { translate } = useTerminology();
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;
  const [searchQuery, setSearchQuery] = useState('');

  const { data, loading, error, refetch } = useListResourcesQuery({
    variables: { tenantId, limit: 100 },
    skip,
  });

  const resources = useMemo(() => {
    return data?.listResources?.items ?? [];
  }, [data]);

  const filteredResources = useMemo(() => {
    if (!searchQuery) return resources;
    const query = searchQuery.toLowerCase();
    return resources.filter((r: ResourceFieldsFragment) =>
      r.resourceName.toLowerCase().includes(query) ||
      r.resourceCode.toLowerCase().includes(query) ||
      r.resourceType?.toLowerCase().includes(query) ||
      r.resourceCategory?.toLowerCase().includes(query)
    );
  }, [resources, searchQuery]);

  const stats = useMemo(() => {
    const total = resources.length;
    const lifeCritical = resources.filter((r: ResourceFieldsFragment) => r.isLifeCriticalResource).length;
    return { total, lifeCritical };
  }, [resources]);

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view resources.
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Resources">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>{translate('resource')}s</Title>
            <Text c="dimmed" size="sm">
              {translate('resource')} capacity and allocation tracking
            </Text>
          </div>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>

        {/* Stats Cards */}
        <Group gap="md">
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm">
                <IconFileAnalytics size={14} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Total Resources</Text>
                <Text size="xl" fw={700}>{stats.total}</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="red">
                <IconAlertTriangle size={14} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Life-Critical</Text>
                <Text size="xl" fw={700}>{stats.lifeCritical}</Text>
              </div>
            </Group>
          </Card>
        </Group>

        {/* Search */}
        <TextInput
          placeholder="Search resources..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
        />

        {/* Resources Table */}
        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <Paper withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Resource</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Capacity</Table.Th>
                  <Table.Th>Life-Critical</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredResources.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text ta="center" c="dimmed" py="md">
                        No resources found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  filteredResources.slice(0, 50).map((resource: ResourceFieldsFragment) => {
                    const utilization = resource.totalCapacity > 0
                      ? ((resource.totalCapacity - resource.availableCapacity) / resource.totalCapacity) * 100
                      : 0;
                    return (
                      <Table.Tr key={resource.resourceId}>
                        <Table.Td>
                          <Stack gap={0}>
                            <Text size="sm" fw={500} lineClamp={1}>{resource.resourceName}</Text>
                            <Text size="xs" c="dimmed">{resource.resourceCode}</Text>
                          </Stack>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm">{resource.resourceType}</Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={resource.resourceStatus === 'AVAILABLE' ? 'green' : 'yellow'}
                            size="sm"
                          >
                            {resource.resourceStatus}
                          </Badge>
                        </Table.Td>
                        <Table.Td style={{ minWidth: 120 }}>
                          <Stack gap={2}>
                            <Text size="xs" c="dimmed">
                              {resource.availableCapacity}/{resource.totalCapacity}
                            </Text>
                            <Progress
                              value={utilization}
                              size="sm"
                              color={utilization > 80 ? 'red' : utilization > 60 ? 'yellow' : 'green'}
                            />
                          </Stack>
                        </Table.Td>
                        <Table.Td>
                          {resource.isLifeCriticalResource ? (
                            <Badge color="red" size="sm">Yes</Badge>
                          ) : (
                            <Badge color="gray" size="sm" variant="light">No</Badge>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        )}

        <Badge variant="light" color="cyan" style={{ alignSelf: 'flex-start' }}>
          OPTIMIZE Phase
        </Badge>
      </Stack>
    </Container>
  );
}

export default ReportListPage;
